import { useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  ToggleGroup,
  ToggleGroupItem,
} from '@kognitos/lattice';
import { V1SupportPermissionLevel } from '@/generated/hooks/agora/models/v1SupportPermissionLevel';
import type { V1SupportAccessGrantBody } from '@/generated/hooks/agora/models/v1SupportAccessGrantBody';
import { useListWorkspaces } from '@/generated/hooks/agora/v1/workspace';
import {
  buildTtl,
  DURATION_PRESETS,
  MAX_DURATION_DAYS,
  MAX_DURATION_HOURS,
  type TDurationPreset,
  type TDurationUnit,
} from '../utils/duration';
import type { TGrantScopeKind, TSupportAccessScope } from '../types';
import { AutomationsMultiSelect } from './AutomationsMultiSelect';
import { parseResourceName } from '@/shared/utils';

interface IAutomationContext {
  automationId: string;
  automationDisplayName: string;
}

interface IGrantSupportAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: TSupportAccessScope;
  organizationId: string;
  workspaceId?: string;
  isPending: boolean;
  onSubmit: (
    body: V1SupportAccessGrantBody,
    workspaceForAutomation?: string,
  ) => Promise<void> | void;
  automationContext?: IAutomationContext;
}

const FORM_ID = 'grant-support-access-form';

const presetTranslationKey: Record<
  Exclude<TDurationPreset, 'custom'>,
  string
> = {
  '1h': 'settings.supportAccess.dialog.duration.preset.oneHour',
  '24h': 'settings.supportAccess.dialog.duration.preset.twentyFourHours',
  '7d': 'settings.supportAccess.dialog.duration.preset.sevenDays',
};

const orgScopeKinds: TGrantScopeKind[] = [
  'currentOrganization',
  'workspace',
  'automation',
];

const workspaceScopeKinds: TGrantScopeKind[] = [
  'currentWorkspace',
  'draftsAndAutomations',
];

const PERMISSION_OPTIONS = [
  {
    value: V1SupportPermissionLevel.SUPPORT_PERMISSION_LEVEL_VIEW_ONLY,
    labelKey: 'settings.supportAccess.dialog.permission.viewOnly.label',
    descKey: 'settings.supportAccess.dialog.permission.viewOnly.description',
  },
  {
    value: V1SupportPermissionLevel.SUPPORT_PERMISSION_LEVEL_VIEW_AND_EDIT,
    labelKey: 'settings.supportAccess.dialog.permission.viewAndEdit.label',
    descKey: 'settings.supportAccess.dialog.permission.viewAndEdit.description',
  },
] as const;

const isAutomationLikeScope = (kind: TGrantScopeKind): boolean =>
  kind === 'automation' || kind === 'draftsAndAutomations';

const requiresWorkspacePicker = (
  kind: TGrantScopeKind,
  scope: TSupportAccessScope,
): boolean => {
  if (scope === 'workspace') return false;
  return kind === 'workspace' || kind === 'automation';
};

type TGrantFormData = {
  scopeKind: TGrantScopeKind;
  selectedWorkspaceName: string;
  selectedAutomationIds: string[];
  permission: V1SupportPermissionLevel;
  durationPreset: TDurationPreset;
  customAmount: string;
  customUnit: TDurationUnit;
};

const grantSchema = z.object({
  scopeKind: z.enum([
    'currentOrganization',
    'workspace',
    'automation',
    'currentWorkspace',
    'draftsAndAutomations',
  ]),
  selectedWorkspaceName: z.string(),
  selectedAutomationIds: z.array(z.string()),
  permission: z.nativeEnum(V1SupportPermissionLevel),
  durationPreset: z.enum(['1h', '24h', '7d', 'custom']),
  customAmount: z.string(),
  customUnit: z.enum(['hours', 'days']),
});

const buildDefaults = (params: {
  scope: TSupportAccessScope;
  isAutomationLocked: boolean;
  lockedWorkspacePath: string;
  lockedAutomationName: string;
}): TGrantFormData => {
  const initialKind: TGrantScopeKind = params.isAutomationLocked
    ? 'draftsAndAutomations'
    : params.scope === 'org'
      ? 'currentOrganization'
      : 'currentWorkspace';

  return {
    scopeKind: initialKind,
    selectedWorkspaceName: params.isAutomationLocked
      ? params.lockedWorkspacePath
      : '',
    selectedAutomationIds: params.isAutomationLocked
      ? [params.lockedAutomationName]
      : [],
    permission: V1SupportPermissionLevel.SUPPORT_PERMISSION_LEVEL_VIEW_ONLY,
    durationPreset: '24h',
    customAmount: '',
    customUnit: 'days',
  };
};

export const GrantSupportAccessDialog = ({
  open,
  onOpenChange,
  scope,
  organizationId,
  workspaceId,
  isPending,
  onSubmit,
  automationContext,
}: IGrantSupportAccessDialogProps) => {
  const { t } = useTranslation('settings');

  const isAutomationLocked = !!automationContext && !!workspaceId;

  const lockedWorkspacePath = useMemo(
    () =>
      isAutomationLocked
        ? `organizations/${organizationId}/workspaces/${workspaceId}`
        : '',
    [isAutomationLocked, organizationId, workspaceId],
  );

  const lockedAutomationName = useMemo(
    () =>
      isAutomationLocked && automationContext
        ? `${lockedWorkspacePath}/automations/${automationContext.automationId}`
        : '',
    [isAutomationLocked, lockedWorkspacePath, automationContext],
  );

  const defaults = useMemo(
    () =>
      buildDefaults({
        scope,
        isAutomationLocked,
        lockedWorkspacePath,
        lockedAutomationName,
      }),
    [scope, isAutomationLocked, lockedWorkspacePath, lockedAutomationName],
  );

  const form = useForm<TGrantFormData>({
    resolver: zodResolver(grantSchema),
    defaultValues: defaults,
    mode: 'onChange',
  });

  const scopeKind = useWatch({ control: form.control, name: 'scopeKind' });
  const selectedWorkspaceName = useWatch({
    control: form.control,
    name: 'selectedWorkspaceName',
  });
  const selectedAutomationIds = useWatch({
    control: form.control,
    name: 'selectedAutomationIds',
  });
  const durationPreset = useWatch({
    control: form.control,
    name: 'durationPreset',
  });
  const customAmount = useWatch({
    control: form.control,
    name: 'customAmount',
  });
  const customUnit = useWatch({ control: form.control, name: 'customUnit' });

  // Reset form state only on the closed→open transition.
  const wasOpenRef = useRef(false);
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative reset of form state when the dialog visibility flips
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      form.reset(defaults);
    }
    wasOpenRef.current = open;
  }, [open, defaults, form]);

  const showWorkspacePicker =
    !isAutomationLocked && requiresWorkspacePicker(scopeKind, scope);
  const showAutomationsPicker =
    !isAutomationLocked && isAutomationLikeScope(scopeKind);
  const showScopeSelect = !isAutomationLocked;

  const { data: workspacesData } = useListWorkspaces(
    organizationId,
    undefined,
    {
      query: {
        enabled: open && scope === 'org' && showWorkspacePicker,
      },
    },
  );
  const workspaceOptions = useMemo(() => {
    return (workspacesData?.data?.workspaces || [])
      .filter(w => !!w.name)
      .map(w => ({
        id: w.name as string,
        label: w.title || (w.name as string),
      }));
  }, [workspacesData]);

  const automationParentWorkspace =
    scope === 'workspace' && workspaceId
      ? `organizations/${organizationId}/workspaces/${workspaceId}`
      : selectedWorkspaceName;

  const parsedAutomationWorkspaceId =
    parseResourceName(automationParentWorkspace).workspace || '';

  const handleSubmit = async (data: TGrantFormData) => {
    const customAmountNumber =
      data.customAmount === '' ? null : Number(data.customAmount);
    const ttl = buildTtl(
      data.durationPreset,
      customAmountNumber,
      data.customUnit,
    );
    if (!ttl) return;

    const body: V1SupportAccessGrantBody = {
      permission_level: data.permission,
      ttl,
    };

    if (data.scopeKind === 'currentOrganization') {
      body.organization_scope = {
        organization: `organizations/${organizationId}`,
      };
    } else if (data.scopeKind === 'workspace') {
      body.workspace_scope = { workspace: data.selectedWorkspaceName };
    } else if (data.scopeKind === 'currentWorkspace') {
      if (!workspaceId) return;
      body.workspace_scope = {
        workspace: `organizations/${organizationId}/workspaces/${workspaceId}`,
      };
    } else {
      // automation or draftsAndAutomations
      const parentWorkspace =
        scope === 'workspace' && workspaceId
          ? `organizations/${organizationId}/workspaces/${workspaceId}`
          : data.selectedWorkspaceName;
      if (!parentWorkspace) return;
      body.automation_scope = {
        workspace: parentWorkspace,
        automation_ids: data.selectedAutomationIds,
      };
    }

    await onSubmit(body, automationParentWorkspace || undefined);
  };

  const renderScopeOption = (kind: TGrantScopeKind) => {
    const labelKey =
      scope === 'org'
        ? `settings.supportAccess.dialog.scopeOptions.org.${kind}`
        : `settings.supportAccess.dialog.scopeOptions.workspace.${kind}`;
    return (
      <SelectItem key={kind} value={kind}>
        {t(labelKey)}
      </SelectItem>
    );
  };

  const scopeOptions = scope === 'org' ? orgScopeKinds : workspaceScopeKinds;

  const customAmountNumber = customAmount === '' ? null : Number(customAmount);
  const ttl = buildTtl(durationPreset, customAmountNumber, customUnit);

  const isSubmitDisabled = (() => {
    if (isPending) return true;
    if (!ttl) return true;
    if (showWorkspacePicker && !selectedWorkspaceName) return true;
    if (showAutomationsPicker) {
      if (selectedAutomationIds.length === 0) return true;
      if (!parsedAutomationWorkspaceId) return true;
    }
    return false;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100vh-4rem)] flex-col sm:max-w-xl lg:max-h-[calc(100vh-4rem)]">
        <DialogHeader>
          <DialogTitle>
            {t(
              isAutomationLocked
                ? 'settings.supportAccess.dialog.automation.title'
                : 'settings.supportAccess.dialog.title',
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              isAutomationLocked
                ? 'settings.supportAccess.dialog.automation.description'
                : 'settings.supportAccess.dialog.description',
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            id={FORM_ID}
            onSubmit={form.handleSubmit(handleSubmit)}
            className="min-h-0 flex-1 overflow-y-auto -mr-2"
          >
            <Flex direction="column" gap={5}>
              <Alert variant="default">
                <Icon type="Info" size="sm" />
                <AlertDescription>
                  {t('settings.supportAccess.dialog.alert')}
                </AlertDescription>
              </Alert>

              {isAutomationLocked && automationContext && (
                <Flex gap={2} align="center" wrap="wrap">
                  <Badge variant="secondary">
                    {t(
                      'settings.supportAccess.dialog.automation.scopeBadgeKind',
                    )}
                  </Badge>
                  <Badge variant="outline">
                    {automationContext.automationDisplayName}
                  </Badge>
                </Flex>
              )}

              {showScopeSelect && (
                <FormField
                  control={form.control}
                  name="scopeKind"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel htmlFor="support-access-scope">
                        {t('settings.supportAccess.dialog.fields.scope')}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={next => {
                            field.onChange(next as TGrantScopeKind);
                            form.setValue('selectedWorkspaceName', '', {
                              shouldValidate: true,
                            });
                            form.setValue('selectedAutomationIds', [], {
                              shouldValidate: true,
                            });
                          }}
                        >
                          <SelectTrigger
                            className="w-full"
                            id="support-access-scope"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {scopeOptions.map(renderScopeOption)}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {showWorkspacePicker && (
                <FormField
                  control={form.control}
                  name="selectedWorkspaceName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel htmlFor="support-access-workspace">
                        {t('settings.supportAccess.dialog.fields.workspace')}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={next => {
                            field.onChange(next);
                            form.setValue('selectedAutomationIds', [], {
                              shouldValidate: true,
                            });
                          }}
                        >
                          <SelectTrigger
                            className="w-full"
                            id="support-access-workspace"
                          >
                            <SelectValue
                              placeholder={t(
                                'settings.supportAccess.dialog.placeholders.selectWorkspace',
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {workspaceOptions.map(option => (
                              <SelectItem key={option.id} value={option.id}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {showAutomationsPicker && (
                <FormField
                  control={form.control}
                  name="selectedAutomationIds"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t(
                          'settings.supportAccess.dialog.fields.draftsAndAutomations',
                        )}
                      </FormLabel>
                      <FormControl>
                        <AutomationsMultiSelect
                          organizationId={organizationId}
                          workspaceId={parsedAutomationWorkspaceId}
                          selected={field.value}
                          onChange={values =>
                            field.onChange(values, { shouldValidate: true })
                          }
                          disabled={
                            scope === 'org' &&
                            scopeKind === 'automation' &&
                            !selectedWorkspaceName
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="permission"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('settings.supportAccess.dialog.fields.permission')}
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={next =>
                          field.onChange(next as V1SupportPermissionLevel)
                        }
                        className="gap-3"
                      >
                        {PERMISSION_OPTIONS.map(item => {
                          const id = `permission-${item.value}`;
                          const isChecked = field.value === item.value;
                          return (
                            <Flex
                              key={item.value}
                              gap={3}
                              align="start"
                              onClick={() => field.onChange(item.value)}
                              className={`cursor-pointer rounded-md border p-3 ${
                                isChecked
                                  ? 'border-primary bg-primary/10'
                                  : 'border-border'
                              }`}
                            >
                              <RadioGroupItem id={id} value={item.value} />
                              <Flex
                                direction="column"
                                gap={1}
                                className="flex-1"
                              >
                                <Label htmlFor={id} className="cursor-pointer">
                                  {t(item.labelKey)}
                                </Label>
                                <Text color="muted" className="text-sm">
                                  {t(item.descKey)}
                                </Text>
                              </Flex>
                            </Flex>
                          );
                        })}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="durationPreset"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      {t('settings.supportAccess.dialog.fields.duration')}
                    </FormLabel>
                    <FormControl>
                      <ToggleGroup
                        type="single"
                        value={field.value}
                        onValueChange={value => {
                          if (value) field.onChange(value as TDurationPreset);
                        }}
                        className="w-full grid grid-cols-2 gap-2"
                      >
                        {DURATION_PRESETS.map(preset => (
                          <ToggleGroupItem
                            key={preset}
                            value={preset}
                            variant="outline"
                            className="w-full rounded-md! border-l!"
                          >
                            {t(presetTranslationKey[preset])}
                          </ToggleGroupItem>
                        ))}
                        <ToggleGroupItem
                          value="custom"
                          variant="outline"
                          className="w-full rounded-md! border-l!"
                        >
                          {t(
                            'settings.supportAccess.dialog.duration.preset.custom',
                          )}
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </FormControl>

                    {durationPreset === 'custom' && (
                      <Flex gap={2} className="mt-2">
                        <FormField
                          control={form.control}
                          name="customAmount"
                          render={({ field: amountField }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={
                                    customUnit === 'hours'
                                      ? MAX_DURATION_HOURS
                                      : MAX_DURATION_DAYS
                                  }
                                  placeholder={t(
                                    'settings.supportAccess.dialog.duration.customPlaceholder',
                                  )}
                                  value={amountField.value}
                                  onChange={event =>
                                    amountField.onChange(
                                      event.currentTarget.value,
                                    )
                                  }
                                  className="w-64"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="customUnit"
                          render={({ field: unitField }) => (
                            <FormItem>
                              <FormControl>
                                <Select
                                  value={unitField.value}
                                  onValueChange={next =>
                                    unitField.onChange(next as TDurationUnit)
                                  }
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="hours">
                                      {t(
                                        'settings.supportAccess.dialog.duration.unit.hours',
                                      )}
                                    </SelectItem>
                                    <SelectItem value="days">
                                      {t(
                                        'settings.supportAccess.dialog.duration.unit.days',
                                      )}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </Flex>
                    )}

                    <Text color="muted" className="text-sm">
                      {t('settings.supportAccess.dialog.duration.helper')}
                    </Text>
                  </FormItem>
                )}
              />
            </Flex>
          </form>
        </Form>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            type="button"
          >
            {t('settings.supportAccess.actions.cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={isSubmitDisabled}
            isLoading={isPending}
          >
            {t('settings.supportAccess.actions.grantAccess')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
